import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Image,
  Input,
  Link,
  Progress,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { FaFacebookF, FaLinkedinIn, FaTwitter } from "react-icons/fa";
import {
  FiCheckCircle,
  FiCopy,
  FiExternalLink,
  FiHeart,
  FiInfo,
  FiLock,
  FiShield,
} from "react-icons/fi";
import { secureFetch } from "../data/secureFetch";
import { donorSecureFetch, getDonorToken } from "../data/donorAuth";

const stripePublishableKey =
  `${process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""}`.trim();
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : null;
const missingStripePublishableKeyMessage =
  "Stripe is not configured. REACT_APP_STRIPE_PUBLISHABLE_KEY is required.";

const dollarsToCents = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.round(parsed * 100);
};

const centsToDollars = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "0.00";
  return (amount / 100).toFixed(2);
};

const normalizeRedirectUrl = (raw) => {
  const value = `${raw || ""}`.trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) {
    return value.replace(/^http:\/\//i, "https://");
  }
  return `https://${value}`;
};

const buildPublicDonationUrl = (slug) => {
  const normalizedSlug = `${slug || ""}`.trim().replace(/^\/+/, "");
  if (!normalizedSlug) return "";
  return `/donate/${normalizedSlug}`;
};

const isHttpUrl = (value = "") => /^https?:\/\//i.test(`${value || ""}`.trim());

const formatDateLabel = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const buildSharePlatformUrls = ({ url, message }) => {
  const shareUrl = `${url || ""}`.trim();
  const shareMessage = `${message || ""}`.trim();
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedMessage = encodeURIComponent(shareMessage);

  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedMessage}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
  };
};

const DONATION_PRESETS = [25, 50, 100, 250, 500];

const FREQUENCY_OPTIONS = ["One-time", "Monthly", "Yearly"];

const FALLBACK_TIP_API_RESPONSE = {
  options: [
    { value: "0.09", label: "9%" },
    { value: "0.05", label: "5%" },
    { value: "0.03", label: "3%" },
    { value: "custom", label: "Custom" },
  ],
  default: "0.09",
  nudge: {
    message: "Would you consider adding a small tip to support our platform?",
    ctaLabel: "Yes, I'll help!",
  },
};

/** Map API tip option value to internal mode key (percent as string, or "custom"). */
const apiTipValueToMode = (value) => {
  const raw = `${value || ""}`.trim().toLowerCase();
  if (raw === "custom") return "custom";
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "custom";
  if (n > 0 && n <= 1) return String(Math.round(n * 100));
  return String(Math.round(n));
};

const normalizeTipConfig = (raw) => {
  const base = raw && raw.options ? raw : FALLBACK_TIP_API_RESPONSE;
  const options = Array.isArray(base.options) ? base.options : [];
  const mapped = options
    .map((o) => {
      const label = `${o?.label || ""}`.trim() || "Tip";
      const mode = apiTipValueToMode(o?.value);
      return { mode, label, rawValue: o?.value };
    })
    .filter((o) => o.mode === "custom" || /^\d+$/.test(o.mode));

  if (!mapped.some((o) => o.mode === "custom")) {
    mapped.push({ mode: "custom", label: "Custom", rawValue: "custom" });
  }

  const defaultMode = apiTipValueToMode(base.default);
  const defaultExists = mapped.some((o) => o.mode === defaultMode);
  const resolvedDefault = defaultExists
    ? defaultMode
    : mapped.find((o) => /^\d+$/.test(o.mode))?.mode || mapped[0]?.mode || "9";

  return {
    options: mapped,
    defaultMode: resolvedDefault,
    nudge: {
      message:
        `${base?.nudge?.message || FALLBACK_TIP_API_RESPONSE.nudge.message}`.trim(),
      ctaLabel:
        `${base?.nudge?.ctaLabel || FALLBACK_TIP_API_RESPONSE.nudge.ctaLabel}`.trim(),
    },
  };
};

const IMPACT_LABELS = {
  25: "Helps cover immediate program needs",
  50: "Supports today's on-the-ground work",
  100: "Funds a stronger week of outreach",
  250: "Moves a major campaign goal forward",
  500: "Powers sustained nonprofit impact",
};

const StripeCheckoutPanel = ({
  basePayload,
  paymentIntentId,
  onSuccess,
  onError,
  isSubmitting,
  setIsSubmitting,
  requester,
  returnUrl,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleConfirm = useCallback(async () => {
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: "if_required",
      });

      if (result.error) {
        onError(result.error.message || "Payment confirmation failed");
        return;
      }

      const resolvedIntentId = result.paymentIntent?.id || paymentIntentId;
      if (!resolvedIntentId) {
        onError("Payment succeeded but no payment intent ID was returned.");
        return;
      }

      const completion = await requester("/api/process-payment/complete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          ...basePayload,
          paymentIntentId: resolvedIntentId,
        },
      });

      if (!completion.ok) {
        throw new Error(completion.message || "Payment completion failed");
      }

      onSuccess(completion);
    } catch (error) {
      onError(error.message || "Payment failed");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    stripe,
    elements,
    setIsSubmitting,
    onError,
    paymentIntentId,
    basePayload,
    onSuccess,
    requester,
    returnUrl,
  ]);

  const paymentElementOptions = useMemo(
    () => ({
      layout: "tabs",
      wallets: {
        applePay: "auto",
        googlePay: "auto",
      },
      defaultValues: {
        billingDetails: {
          name:
            `${basePayload?.firstName || ""} ${basePayload?.lastName || ""}`.trim() ||
            undefined,
          email: basePayload?.email || undefined,
          phone: basePayload?.phoneNumber || undefined,
          address: {
            line1: basePayload?.addressLine1 || undefined,
            line2: basePayload?.addressLine2 || undefined,
            city: basePayload?.city || undefined,
            state: basePayload?.state || undefined,
            postal_code: basePayload?.zipCode || undefined,
            country: "US",
          },
        },
      },
    }),
    [basePayload],
  );

  return (
    <Stack spacing={4}>
      <Text fontWeight="600">Payment</Text>
      <ExpressCheckoutElement
        options={{
          buttonType: {
            applePay: "donate",
            googlePay: "donate",
          },
        }}
      />
      <PaymentElement options={paymentElementOptions} />
      <Button
        colorScheme="orange"
        onClick={handleConfirm}
        isLoading={isSubmitting}
        isDisabled={!stripe || !elements || isSubmitting}
      >
        Complete Donation
      </Button>
      <Text fontSize="sm" color="gray.500">
        Your payment is processed securely by Stripe.
      </Text>
    </Stack>
  );
};

const initialFormState = {
  email: "",
  firstName: "",
  lastName: "",
  donationAmount: "25.00",
  tipAmount: "0.00",
  donationType: "One-time",
  note: "",
  phoneNumber: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zipCode: "",
  savePaymentMethodForFuture: true,
  saveProfileForFuture: false,
};

const pickConnectedAccountId = (row) =>
  row?.stripe_connected_account_id || null;

const normalizeCampaignArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value.filter((campaign) => Number.isInteger(Number(campaign?.id)));
};

const DonationFormStripe = ({
  campaignId: campaignIdProp,
  orgSlug: orgSlugProp,
  hideCampaignDetails = false,
  successRedirect = "",
}) => {
  const location = useLocation();
  const {
    url,
    campaignId: campaignIdParam,
    orgSlug: orgSlugParam,
  } = useParams();
  const authenticatedOrganizationId = useSelector(
    (state) => state.user.organizationId,
  );

  const resolvedCampaignId = campaignIdParam || campaignIdProp || null;
  const resolvedOrgSlug = orgSlugParam || orgSlugProp || null;
  const resolvedUrl = url || null;
  const queryRedirect = useMemo(() => {
    return normalizeRedirectUrl(
      new URLSearchParams(location.search).get("redirect") || "",
    );
  }, [location.search]);
  const stepParam = useMemo(
    () => new URLSearchParams(location.search).get("step"),
    [location.search],
  );
  const singlePageMode = stepParam === "false";
  const normalizedSuccessRedirect = useMemo(
    () => normalizeRedirectUrl(successRedirect) || queryRedirect,
    [successRedirect, queryRedirect],
  );
  const stripeReturnUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}${window.location.pathname}${window.location.search}`;
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [target, setTarget] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [form, setForm] = useState(initialFormState);
  const [fundAllocations, setFundAllocations] = useState([
    { campaignId: "", amount: "" }
  ]);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [completionPayload, setCompletionPayload] = useState(null);
  const [intentLoading, setIntentLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [tipMode, setTipMode] = useState("9");
  const [tipCustomAmount, setTipCustomAmount] = useState("");
  const [tipConfigRaw, setTipConfigRaw] = useState(null);
  const tipModeTouchedRef = useRef(false);
  const tipConfigNormalized = useMemo(
    () => normalizeTipConfig(tipConfigRaw),
    [tipConfigRaw],
  );
  const tipPercentModes = useMemo(
    () =>
      tipConfigNormalized.options
        .filter((o) => /^\d+$/.test(o.mode))
        .map((o) => o.mode),
    [tipConfigNormalized.options],
  );
  const [copyLinkStatus, setCopyLinkStatus] = useState("");
  const [hostPageShareUrl, setHostPageShareUrl] = useState("");
  const [embedParentOrigin, setEmbedParentOrigin] = useState("");
  const requester = useCallback(
    (path, options) =>
      (getDonorToken() ? donorSecureFetch : secureFetch)(path, options),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await secureFetch(`/api/tips/config`, {
          method: "GET",
          includeFirebaseAuth: false,
        });
        if (!cancelled && resp.ok && resp.options) {
          setTipConfigRaw(resp);
        }
      } catch {
        if (!cancelled) setTipConfigRaw(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (tipConfigRaw == null || tipModeTouchedRef.current) {
      return;
    }
    const normalized = normalizeTipConfig(tipConfigRaw);
    const def = normalized.defaultMode || "9";
    setTipMode(def);
    if (def === "custom") {
      setTipCustomAmount("");
      setForm((prev) => ({ ...prev, tipAmount: "0.00" }));
      tipModeTouchedRef.current = true;
      return;
    }
    setForm((prev) => {
      const donationCents = dollarsToCents(prev.donationAmount);
      const tipCents = Math.round((donationCents * Number(def)) / 100);
      return { ...prev, tipAmount: (tipCents / 100).toFixed(2) };
    });
    tipModeTouchedRef.current = true;
  }, [tipConfigRaw]);

  const donationAmountCents = useMemo(() => {
    if (target?.mode === "fund_group") {
      return fundAllocations
        .map((row) => dollarsToCents(row.amount || "0"))
        .reduce((sum, amt) => sum + amt, 0);
    }
    return dollarsToCents(form.donationAmount);
  }, [target?.mode, fundAllocations, form.donationAmount]);

  useEffect(() => {
    if (target?.mode === "fund_group") {
      const computedAmount = (donationAmountCents / 100).toFixed(2);
      if (form.donationAmount !== computedAmount) {
        setForm((prev) => ({
          ...prev,
          donationAmount: computedAmount,
        }));
      }
    }
  }, [target?.mode, donationAmountCents, form.donationAmount]);

  const totalCents = useMemo(() => {
    const tip = dollarsToCents(form.tipAmount || "0");
    return donationAmountCents + tip;
  }, [donationAmountCents, form.tipAmount]);

  useEffect(() => {
    if (!tipPercentModes.includes(tipMode)) return;

    const tipCents = Math.round((donationAmountCents * Number(tipMode)) / 100);
    const nextTipAmount = (tipCents / 100).toFixed(2);

    setForm((prev) =>
      prev.tipAmount === nextTipAmount
        ? prev
        : {
            ...prev,
            tipAmount: nextTipAmount,
          },
    );
  }, [donationAmountCents, tipMode, tipPercentModes]);

  const donationOptions = useMemo(() => {
    if (!target) return [];
    if (target.mode === "campaign") return [target.campaign];
    return normalizeCampaignArray(target.campaigns);
  }, [target]);

  useEffect(() => {
    if (donationOptions.length === 0) {
      setSelectedCampaignId("");
      return;
    }

    const selectedExists = donationOptions.some(
      (campaign) => String(campaign.id) === String(selectedCampaignId),
    );
    if (!selectedExists) {
      setSelectedCampaignId(String(donationOptions[0].id));
    }
  }, [donationOptions, selectedCampaignId]);

  useEffect(() => {
    const loadFromCampaignRow = (campaignRow) => {
      setTarget({
        mode: "campaign",
        organizationId: campaignRow.organization_id,
        campaign: campaignRow,
        connectedAccountId: pickConnectedAccountId(campaignRow),
        name: campaignRow.name,
        description: campaignRow.description || "",
        organizationName: campaignRow.organization_name || "",
        organizationDonationUrl: campaignRow.organization_donation_url || "",
        organizationWebsiteUrl: campaignRow.organization_website_url || "",
      });
      setSelectedCampaignId(String(campaignRow.id));
    };

    const loadTarget = async () => {
      setLoading(true);
      setError("");

      try {
        if (resolvedOrgSlug) {
          const orgResp = await secureFetch(
            `/api/organizations/donation/${resolvedOrgSlug}`,
          );
          if (!orgResp.ok) {
            throw new Error(orgResp.message || "Organization not found");
          }

          const campaigns = normalizeCampaignArray(orgResp.campaigns);
          setTarget({
            mode: "organization",
            organizationId: orgResp.organization.id,
            campaigns,
            connectedAccountId: pickConnectedAccountId(orgResp.organization),
            name: orgResp.organization.name,
            description: orgResp.organization.description || "",
            organizationName: orgResp.organization.name || "",
            organizationDonationUrl: orgResp.organization.donation_url || "",
            organizationWebsiteUrl: orgResp.organization.website_url || "",
          });
          return;
        }

        if (resolvedUrl) {
          const campaignResp = await secureFetch(
            `/api/campaigns/url/${resolvedUrl}`,
          );
          if (campaignResp.ok) {
            loadFromCampaignRow(campaignResp);
            return;
          }

          const fundGroupResp = await secureFetch(
            `/api/fund-groups/url/${resolvedUrl}`,
          );
          if (fundGroupResp.ok) {
            const campaigns = normalizeCampaignArray(fundGroupResp.campaigns);
            setTarget({
              mode: "fund_group",
              organizationId: fundGroupResp.organization_id,
              fundGroupId: fundGroupResp.id,
              campaigns,
              connectedAccountId: pickConnectedAccountId(fundGroupResp),
              name: fundGroupResp.title,
              description: fundGroupResp.description || "",
              organizationName: fundGroupResp.organization_name || "",
              organizationDonationUrl:
                fundGroupResp.organization_donation_url || "",
              organizationWebsiteUrl:
                fundGroupResp.organization_website_url || "",
            });
            setFundAllocations([
              { campaignId: campaigns[0] ? String(campaigns[0].id) : "", amount: "" }
            ]);
            return;
          }

          const orgResp = await secureFetch(
            `/api/organizations/donation/${resolvedUrl}`,
          );
          if (orgResp.ok) {
            const campaigns = normalizeCampaignArray(orgResp.campaigns);
            setTarget({
              mode: "organization",
              organizationId: orgResp.organization.id,
              campaigns,
              connectedAccountId: pickConnectedAccountId(orgResp.organization),
              name: orgResp.organization.name,
              description: orgResp.organization.description || "",
              organizationName: orgResp.organization.name || "",
              organizationDonationUrl: orgResp.organization.donation_url || "",
              organizationWebsiteUrl: orgResp.organization.website_url || "",
            });
            return;
          }

          throw new Error(
            campaignResp.message ||
              fundGroupResp.message ||
              orgResp.message ||
              "Donation target not found",
          );
        }

        if (resolvedCampaignId) {
          const publicCampaign = await secureFetch(
            `/api/campaigns/public/id/${resolvedCampaignId}`,
          );
          if (publicCampaign.ok) {
            loadFromCampaignRow(publicCampaign);
            return;
          }

          const privateCampaign = await secureFetch(
            `/api/campaigns/id/${resolvedCampaignId}`,
          );
          if (privateCampaign.ok) {
            loadFromCampaignRow(privateCampaign);
            return;
          }

          throw new Error(
            publicCampaign.message ||
              privateCampaign.message ||
              "Campaign not found",
          );
        }

        if (authenticatedOrganizationId) {
          const [organizationResp, campaignsResp] = await Promise.all([
            secureFetch(`/api/organizations/${authenticatedOrganizationId}`),
            secureFetch(
              `/api/campaigns/organization/${authenticatedOrganizationId}`,
            ),
          ]);

          if (!organizationResp.ok) {
            throw new Error(
              organizationResp.message || "Unable to load organization context",
            );
          }

          const campaigns = normalizeCampaignArray(
            campaignsResp.data || campaignsResp.campaigns || campaignsResp,
          );

          setTarget({
            mode: "organization",
            organizationId: organizationResp.id,
            campaigns,
            connectedAccountId: pickConnectedAccountId(organizationResp),
            name: organizationResp.name,
            description: organizationResp.description || "",
            organizationName: organizationResp.name || "",
            organizationDonationUrl: organizationResp.donation_url || "",
            organizationWebsiteUrl: organizationResp.website_url || "",
          });
          return;
        }

        throw new Error("Donation URL is missing.");
      } catch (loadError) {
        setError(loadError.message || "Failed to load donation page");
      } finally {
        setLoading(false);
      }
    };

    loadTarget();
  }, [
    resolvedOrgSlug,
    resolvedUrl,
    resolvedCampaignId,
    authenticatedOrganizationId,
  ]);

  useEffect(() => {
    const handleHostPageMessage = (event) => {
      if (window.parent && event.source !== window.parent) return;

      const payload = event?.data;
      if (!payload || payload.source !== "purelygive-embed-host") return;
      if (payload.type !== "parent") return;
      if (!isHttpUrl(payload.url)) return;

      const parentOriginFromPayload = (() => {
        try {
          return new URL(payload.url).origin;
        } catch {
          return "";
        }
      })();

      if (
        !parentOriginFromPayload ||
        event.origin !== parentOriginFromPayload
      ) {
        return;
      }

      setHostPageShareUrl(payload.url);
      setEmbedParentOrigin(parentOriginFromPayload);
    };

    window.addEventListener("message", handleHostPageMessage);
    return () => {
      window.removeEventListener("message", handleHostPageMessage);
    };
  }, []);

  const selectedCampaign = useMemo(
    () =>
      donationOptions.find(
        (campaign) => String(campaign.id) === String(selectedCampaignId),
      ) || null,
    [donationOptions, selectedCampaignId],
  );

  const organizationName =
    selectedCampaign?.organization_name || target?.organizationName || "";
  const organizationDonationLink = buildPublicDonationUrl(
    selectedCampaign?.organization_donation_url ||
      target?.organizationDonationUrl ||
      "",
  );
  const organizationLandingLink =
    target?.organizationId !== undefined && target?.organizationId !== null
      ? `/org/${encodeURIComponent(String(target.organizationId))}`
      : organizationDonationLink;
  const organizationWebsiteLink = normalizeRedirectUrl(
    selectedCampaign?.organization_website_url ||
      target?.organizationWebsiteUrl ||
      "",
  );
  const campaignLink = buildPublicDonationUrl(selectedCampaign?.url || "");
  const displayTitle =
    target?.mode === "organization"
      ? target?.name || selectedCampaign?.name || "this nonprofit"
      : selectedCampaign?.name || target?.name || "this nonprofit";
  const displayDescription = (
    target?.mode === "organization"
      ? target?.description || selectedCampaign?.description || ""
      : selectedCampaign?.description || target?.description || ""
  ).trim();
  const displayBannerUrl = selectedCampaign?.banner_image_url || "";
  const campaignEndDateLabel = formatDateLabel(selectedCampaign?.end_date);
  const goalCents = Number(selectedCampaign?.goal || 0);
  const raisedCents = Number(selectedCampaign?.amount_raised || 0);
  const progressPercent =
    goalCents > 0
      ? Math.max(0, Math.min(100, Math.round((raisedCents / goalCents) * 100)))
      : 0;

  const resolvedShareUrl = useMemo(() => {
    if (hideCampaignDetails && isHttpUrl(hostPageShareUrl)) {
      return hostPageShareUrl;
    }

    if (typeof window === "undefined") return "";

    if (selectedCampaign?.url) {
      return `${window.location.origin}/donate/${selectedCampaign.url}`;
    }

    if (target?.organizationDonationUrl) {
      return `${window.location.origin}/donate/${target.organizationDonationUrl}`;
    }

    return `${window.location.origin}${window.location.pathname}${window.location.search}`;
  }, [
    hideCampaignDetails,
    hostPageShareUrl,
    selectedCampaign?.url,
    target?.organizationDonationUrl,
  ]);

  const shareMessage = useMemo(
    () =>
      `I just donated to ${displayTitle} through PurelyGive. Join me in supporting this cause.`,
    [displayTitle],
  );

  const shareLinks = useMemo(
    () =>
      buildSharePlatformUrls({
        url: resolvedShareUrl,
        message: shareMessage,
      }),
    [resolvedShareUrl, shareMessage],
  );

  const resolveSuccessRedirect = useCallback(() => {
    if (normalizedSuccessRedirect) return normalizedSuccessRedirect;
    return "";
  }, [normalizedSuccessRedirect]);

  const applyPresetDonationAmount = (amount) => {
    setForm((prev) => ({
      ...prev,
      donationAmount: `${Number(amount || 0).toFixed(2)}`,
    }));
  };

  const selectTipMode = (mode) => {
    tipModeTouchedRef.current = true;
    setTipMode(mode);

    if (mode === "custom") {
      const customTipCents = dollarsToCents(tipCustomAmount || "0");
      setForm((prev) => ({
        ...prev,
        tipAmount: (customTipCents / 100).toFixed(2),
      }));
      return;
    }

    const donationCents =
      target?.mode === "fund_group"
        ? donationAmountCents
        : dollarsToCents(form.donationAmount);
    const tipCents = Math.round((donationCents * Number(mode)) / 100);
    setForm((prev) => ({
      ...prev,
      tipAmount: (tipCents / 100).toFixed(2),
    }));
  };

  const updateCustomTipAmount = (event) => {
    const nextValue = event.target.value;
    tipModeTouchedRef.current = true;
    setTipMode("custom");
    setTipCustomAmount(nextValue);

    const customTipCents = dollarsToCents(nextValue || "0");
    setForm((prev) => ({
      ...prev,
      tipAmount: (customTipCents / 100).toFixed(2),
    }));
  };

  const copyShareLink = async () => {
    if (!resolvedShareUrl || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(resolvedShareUrl);
      setCopyLinkStatus("Copied");
      setTimeout(() => setCopyLinkStatus(""), 1500);
    } catch {
      setCopyLinkStatus("Copy failed");
      setTimeout(() => setCopyLinkStatus(""), 2000);
    }
  };

  const onChangeField = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]:
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value,
    }));
  };

  const addFundAllocationRow = () => {
    setFundAllocations((prev) => [
      ...prev,
      { campaignId: target?.campaigns?.[0]?.id ? String(target.campaigns[0].id) : "", amount: "", sortOrder: prev.length },
    ]);
  };

  const removeFundAllocationRow = (index) => {
    setFundAllocations((prev) =>
      prev
        .filter((_, idx) => idx !== index)
        .map((row, idx) => ({
          ...row,
          sortOrder: idx,
        })),
    );
  };

  const updateFundAllocationRow = (index, key, value) => {
    if (key === "amount" && !/^\d*\.?\d{0,2}$/.test(value)) {
      return;
    }
    setFundAllocations((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [key]: value,
      };
      return next;
    });
  };

  const buildPayload = () => {
    const isFundGroup = target?.mode === "fund_group";
    
    let donationAmount = 0;
    let allocations = [];
    let campaignId = null;

    if (isFundGroup) {
      allocations = fundAllocations
        .map((row, idx) => {
          const cid = parseInt(row.campaignId, 10);
          const amt = dollarsToCents(row.amount || "0");
          return {
            campaignId: cid,
            amount: amt,
            sortOrder: idx,
          };
        })
        .filter((row) => Number.isInteger(row.campaignId) && row.campaignId > 0 && row.amount > 0);

      donationAmount = allocations.reduce((sum, row) => sum + row.amount, 0);

      if (donationAmount <= 0) {
        throw new Error("Please enter a valid donation amount for at least one fund.");
      }

      const hasInvalidRow = fundAllocations.some((row) => {
        const amt = dollarsToCents(row.amount || "0");
        const cid = parseInt(row.campaignId, 10);
        return amt > 0 && (!cid || cid <= 0);
      });
      if (hasInvalidRow) {
        throw new Error("Please select a fund for each donation amount.");
      }

      campaignId = allocations[0]?.campaignId || null;
    } else {
      donationAmount = dollarsToCents(form.donationAmount);
      campaignId = Number(selectedCampaignId);

      if (!campaignId || donationAmount <= 0) {
        throw new Error("Please select a campaign and enter a valid donation amount.");
      }
    }

    const tipAmount = dollarsToCents(form.tipAmount || "0");

    if (!target?.organizationId) {
      throw new Error("Organization ID is required.");
    }

    const tipOptionLabel = (() => {
      if (tipMode === "custom") return "custom";
      const match = tipConfigNormalized.options.find((o) => o.mode === tipMode);
      return match?.label || `${tipMode}%`;
    })();

    const payload = {
      organizationId: target.organizationId,
      campaignId,
      donationAmount,
      platformTipAmount: tipAmount,
      amount: donationAmount + tipAmount,
      donationType: form.donationType,
      sourceType: target.mode,
      fundGroupId: isFundGroup ? target.fundGroupId : null,
      platformTipOption: tipOptionLabel,
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      note: form.note,
      phoneNumber: form.phoneNumber,
      addressLine1: form.addressLine1,
      addressLine2: form.addressLine2,
      city: form.city,
      state: form.state,
      zipCode: form.zipCode,
      savePaymentMethodForFuture: form.savePaymentMethodForFuture,
      saveProfileForFuture: form.saveProfileForFuture,
    };

    if (isFundGroup) {
      payload.allocations = allocations;
    }

    return payload;
  };

  const initializePayment = async () => {
    setError("");
    setResult(null);
    setClientSecret("");
    setPaymentIntentId("");
    setCompletionPayload(null);

    try {
      if (!stripePromise) {
        throw new Error(missingStripePublishableKeyMessage);
      }

      const payload = buildPayload();

      if (!payload.email || !payload.firstName || !payload.lastName) {
        throw new Error("Email, first name, and last name are required.");
      }

      setIntentLoading(true);
      const response = await requester("/api/process-payment/intent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        data: payload,
      });

      if (!response.ok) {
        throw new Error(response.message || "Failed to initialize payment.");
      }

      // Ensure backend returned the required Stripe data
      if (!response.clientSecret || !response.paymentIntentId) {
        console.error("Invalid payment intent response:", response);
        throw new Error(
          response.message ||
            "Payment initialization returned incomplete data.",
        );
      }

      setClientSecret(response.clientSecret);
      setPaymentIntentId(response.paymentIntentId);
      setCompletionPayload(payload);
    } catch (initError) {
      setError(initError.message || "Unable to initialize payment.");
    } finally {
      setIntentLoading(false);
    }
  };

  // If `?step=false` (single-page mode), automatically initialize payment
  // once the required donor fields and a valid donation amount are present.
  const initializePaymentRef = useRef(initializePayment);
  useEffect(() => {
    initializePaymentRef.current = initializePayment;
  });

  useEffect(() => {
    if (!singlePageMode) return;
    if (clientSecret || intentLoading) return;
    const currentDonationCents =
      target?.mode === "fund_group"
        ? donationAmountCents
        : dollarsToCents(form.donationAmount);
    const hasRequired =
      (form.email || "").trim() &&
      (form.firstName || "").trim() &&
      (form.lastName || "").trim() &&
      currentDonationCents > 0;
    if (!hasRequired) return;

    // Fire-and-forget initialization; errors will populate `error` state.
    (async () => {
      try {
        await initializePaymentRef.current();
      } catch (err) {
        // initializePayment already sets error state; keep console trace.
        console.error("Auto initializePayment failed:", err);
      }
    })();
  }, [
    singlePageMode,
    form.email,
    form.firstName,
    form.lastName,
    form.donationAmount,
    donationAmountCents,
    target?.mode,
    clientSecret,
    intentLoading,
  ]);

  const resetPayment = () => {
    setClientSecret("");
    setPaymentIntentId("");
    setCompletionPayload(null);
    setResult(null);
  };

  const platformTipCents = dollarsToCents(form.tipAmount || "0");
  const currentPreset = DONATION_PRESETS.find(
    (amount) => donationAmountCents === amount * 100,
  );
  const impactLabel =
    IMPACT_LABELS[currentPreset] || "A gift of any size makes a difference";
  const summaryTipLabel =
    tipMode === "custom"
      ? `$${centsToDollars(platformTipCents)}`
      : tipPercentModes.includes(tipMode)
        ? `$${centsToDollars(platformTipCents)} (${tipMode}%)`
        : `$${centsToDollars(platformTipCents)}`;

  // Only warn when the donor picked "Custom" and left it empty or 0, which removes
  // the platform tip entirely. Selecting any preset percentage never shows the nudge.
  const showTipNudge =
    tipMode === "custom" &&
    platformTipCents <= 0 &&
    Boolean(tipConfigNormalized.nudge.message);
  const tipNudgeMessage = tipConfigNormalized.nudge.message;
  const donationLocked = Boolean(clientSecret || intentLoading);
  const inputStyles = {
    bg: "#fbfaf7",
    borderColor: "#e4e2dd",
    borderRadius: "12px",
    color: "#3c656c",
    _placeholder: { color: "rgba(60, 101, 108, 0.35)" },
    _focus: {
      borderColor: "#fd6d1a",
      boxShadow: "0 0 0 2px rgba(253, 109, 26, 0.18)",
    },
  };
  const labelStyles = {
    color: "rgba(60, 101, 108, 0.7)",
    fontSize: "xs",
    fontWeight: "800",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  };
  const topBar = !hideCampaignDetails ? (
    <Box maxW="1160px" mx="auto" mb={{ base: 5, lg: 7 }}>
      <HStack
        as="header"
        justify="space-between"
        bg="rgba(255, 255, 255, 0.86)"
        border="1px solid"
        borderColor="#e4e2dd"
        borderRadius="18px"
        px={{ base: 4, md: 5 }}
        py={3}
        boxShadow="0 14px 32px rgba(60, 101, 108, 0.08)"
        backdropFilter="blur(12px)"
      >
        <Link
          as={RouterLink}
          to="/"
          aria-label="PurelyGive home"
          display="inline-flex"
          alignItems="center"
          minW={0}
        >
          <Image
            src="/logoWithTextNoBg.png"
            alt="PurelyGive"
            h={{ base: "30px", md: "34px" }}
            maxW={{ base: "150px", md: "190px" }}
            objectFit="contain"
          />
        </Link>
        <HStack
          color="#3c656c"
          bg="#f5f3ee"
          borderRadius="999px"
          px={3}
          py={2}
          spacing={2}
        >
          <FiShield />
          <Text
            display={{ base: "none", sm: "block" }}
            fontSize="xs"
            fontWeight="800"
            letterSpacing="0.08em"
            textTransform="uppercase"
            whiteSpace="nowrap"
          >
            Secure giving
          </Text>
        </HStack>
      </HStack>
    </Box>
  ) : null;

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (result) {
    return (
      <Box
        bg="#fbf9f4"
        minH={hideCampaignDetails ? "auto" : "100vh"}
        px={4}
        py={8}
      >
        {topBar}
        <VStack spacing={5} align="stretch" maxW="760px" mx="auto">
          <Box
            borderWidth="1px"
            borderColor="#e4e2dd"
            borderRadius="24px"
            p={{ base: 5, md: 8 }}
            bg="white"
            boxShadow="0 20px 45px rgba(60, 101, 108, 0.1)"
          >
            <Stack spacing={5}>
              <HStack spacing={3} align="center">
                <Box
                  w="44px"
                  h="44px"
                  borderRadius="full"
                  bg="#3c656c"
                  color="white"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <FiCheckCircle size={24} />
                </Box>
                <Box>
                  <Text
                    color="rgba(60, 101, 108, 0.55)"
                    fontSize="xs"
                    fontWeight="800"
                    letterSpacing="0.14em"
                    textTransform="uppercase"
                  >
                    Donation complete
                  </Text>
                  <Heading size="lg" color="#3c656c">
                    Thank you for your donation.
                  </Heading>
                </Box>
              </HStack>
              <Text color="rgba(60, 101, 108, 0.78)">
                Your support helps {displayTitle} keep making an impact.
              </Text>
              {result?.donationId ? (
                <Text color="rgba(60, 101, 108, 0.7)">
                  Donation ID: {result.donationId}
                </Text>
              ) : null}
              {result?.paymentStatus ? (
                <Text color="rgba(60, 101, 108, 0.7)">
                  Payment Status: {result.paymentStatus}
                </Text>
              ) : null}

              <Divider />

              <Stack spacing={3}>
                <Text fontWeight="700" color="#3c656c">
                  Share this campaign
                </Text>
                <HStack spacing={3}>
                  <IconButton
                    as="a"
                    href={shareLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    icon={<FaFacebookF />}
                    aria-label="Share on Facebook"
                    isDisabled={!resolvedShareUrl}
                    bg="#f5f3ee"
                    color="#3c656c"
                  />
                  <IconButton
                    as="a"
                    href={shareLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    icon={<FaTwitter />}
                    aria-label="Share on Twitter"
                    isDisabled={!resolvedShareUrl}
                    bg="#f5f3ee"
                    color="#3c656c"
                  />
                  <IconButton
                    as="a"
                    href={shareLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    icon={<FaLinkedinIn />}
                    aria-label="Share on LinkedIn"
                    isDisabled={!resolvedShareUrl}
                    bg="#f5f3ee"
                    color="#3c656c"
                  />
                  <Button
                    leftIcon={<FiCopy />}
                    variant="outline"
                    onClick={copyShareLink}
                    isDisabled={!resolvedShareUrl}
                  >
                    {copyLinkStatus || "Copy Link"}
                  </Button>
                </HStack>
              </Stack>

              <Stack
                direction={{ base: "column", sm: "row" }}
                spacing={3}
                pt={2}
              >
                <Button
                  colorScheme="orange"
                  onClick={() => {
                    resetPayment();
                    const defaultTipMode =
                      tipConfigNormalized.defaultMode || "9";
                    const resetDonationCents = dollarsToCents(
                      initialFormState.donationAmount,
                    );
                    const nextTipAmount =
                      defaultTipMode === "custom"
                        ? "0.00"
                        : (
                            Math.round(
                              (resetDonationCents * Number(defaultTipMode)) /
                                100,
                            ) / 100
                          ).toFixed(2);

                    setForm({
                      ...initialFormState,
                      tipAmount: nextTipAmount,
                    });
                    setTipCustomAmount("");
                    tipModeTouchedRef.current = true;
                    setTipMode(defaultTipMode);
                  }}
                  bg="#fd6d1a"
                  color="white"
                  _hover={{ bg: "#ef5f0d" }}
                >
                  Make Another Donation
                </Button>
                {campaignLink ? (
                  <Button as={RouterLink} to={campaignLink} variant="outline">
                    View Campaign Page
                  </Button>
                ) : organizationLandingLink ? (
                  <Button
                    as={RouterLink}
                    to={organizationLandingLink}
                    variant="outline"
                  >
                    View Organization Page
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Box>
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      bg={hideCampaignDetails ? "transparent" : "#fbf9f4"}
      minH={hideCampaignDetails ? "auto" : "100vh"}
      px={hideCampaignDetails ? 0 : { base: 4, md: 6 }}
      py={hideCampaignDetails ? 0 : { base: 6, lg: 10 }}
    >
      {topBar}
      <SimpleGrid
        columns={{ base: 1, md: hideCampaignDetails ? 1 : 12 }}
        gap={{ base: 5, md: 8 }}
        maxW={hideCampaignDetails ? "760px" : "1160px"}
        mx="auto"
        alignItems="start"
      >
        {!hideCampaignDetails ? (
          <Box
            gridColumn={{ md: "span 5" }}
            position={{ md: "sticky" }}
            top="28px"
          >
            <Box
              bg="white"
              border="1px solid"
              borderColor="#e4e2dd"
              borderRadius="24px"
              overflow="hidden"
              boxShadow="0 20px 45px rgba(60, 101, 108, 0.08)"
            >
              {displayBannerUrl ? (
                <Image
                  src={displayBannerUrl}
                  alt={displayTitle}
                  h={{ base: "220px", md: "280px" }}
                  w="100%"
                  objectFit="cover"
                />
              ) : null}

              <Stack spacing={5} p={{ base: 5, md: 7 }}>
                <Box>
                  {organizationName ? (
                    <Text
                      color="#a23f00"
                      fontSize="xs"
                      fontWeight="800"
                      letterSpacing="0.12em"
                      textTransform="uppercase"
                      mb={2}
                    >
                      {organizationName}
                    </Text>
                  ) : null}
                  <Heading color="#3c656c" size="lg">
                    Support {displayTitle}
                  </Heading>
                </Box>

                <Text color="rgba(60, 101, 108, 0.74)" lineHeight="1.7">
                  {displayDescription ||
                    "Complete your donation securely through PurelyGive."}
                </Text>

                {goalCents > 0 ? (
                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <Text color="#3c656c" fontWeight="800">
                        ${centsToDollars(raisedCents)} raised
                      </Text>
                      <Text color="rgba(60, 101, 108, 0.55)" fontWeight="700">
                        Goal ${centsToDollars(goalCents)}
                      </Text>
                    </HStack>
                    <Progress
                      value={progressPercent}
                      h="10px"
                      borderRadius="full"
                      bg="#f0eee9"
                      sx={{
                        "& > div": {
                          background: "#fd6d1a",
                        },
                      }}
                    />
                    <Text
                      mt={2}
                      color="rgba(60, 101, 108, 0.48)"
                      fontSize="sm"
                      fontWeight="600"
                    >
                      {progressPercent}% funded
                    </Text>
                  </Box>
                ) : null}

                {campaignEndDateLabel ? (
                  <HStack color="rgba(60, 101, 108, 0.64)" fontSize="sm">
                    <FiInfo />
                    <Text>Campaign ends on {campaignEndDateLabel}</Text>
                  </HStack>
                ) : null}

                <Stack spacing={2}>
                  {organizationLandingLink ? (
                    <Link
                      as={RouterLink}
                      to={organizationLandingLink}
                      color="#3c656c"
                      fontWeight="700"
                    >
                      Organization Page
                    </Link>
                  ) : null}
                  {organizationWebsiteLink ? (
                    <Link
                      href={organizationWebsiteLink}
                      isExternal
                      color="#3c656c"
                      fontWeight="700"
                      display="inline-flex"
                      alignItems="center"
                      gap={2}
                    >
                      Organization Website <FiExternalLink />
                    </Link>
                  ) : null}
                  {campaignLink ? (
                    <Link
                      as={RouterLink}
                      to={campaignLink}
                      color="#3c656c"
                      fontWeight="700"
                    >
                      Campaign Link
                    </Link>
                  ) : null}
                </Stack>
              </Stack>
            </Box>
          </Box>
        ) : null}

        <Stack
          gridColumn={{ md: hideCampaignDetails ? "span 1" : "span 7" }}
          spacing={5}
        >
          {error ? (
            <Alert status="error" borderRadius="14px" bg="#fff5f5">
              <AlertIcon />
              {error}
            </Alert>
          ) : null}

          <Box
            bg="white"
            border="1px solid"
            borderColor="#e4e2dd"
            borderRadius="24px"
            p={{ base: 4, md: 6 }}
            boxShadow="0 20px 45px rgba(60, 101, 108, 0.08)"
          >
            <Stack spacing={6}>
              <HStack
                bg="#f5f3ee"
                borderRadius="14px"
                p="1"
                spacing={1}
                align="stretch"
              >
                {FREQUENCY_OPTIONS.map((option) => {
                  const active = form.donationType === option;
                  return (
                    <Button
                      key={option}
                      variant="unstyled"
                      flex="1"
                      h="44px"
                      borderRadius="10px"
                      bg={active ? "white" : "transparent"}
                      color={active ? "#3c656c" : "rgba(60, 101, 108, 0.5)"}
                      boxShadow={
                        active ? "0 1px 4px rgba(0, 0, 0, 0.08)" : "none"
                      }
                      fontWeight={active ? "800" : "700"}
                      isDisabled={donationLocked}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          donationType: option,
                        }))
                      }
                    >
                      {option}
                    </Button>
                  );
                })}
              </HStack>

              {donationOptions.length > 1 && target?.mode !== "fund_group" ? (
                <FormControl isRequired>
                  <FormLabel {...labelStyles}>Campaign</FormLabel>
                  <Select
                    value={selectedCampaignId}
                    onChange={(event) =>
                      setSelectedCampaignId(event.target.value)
                    }
                    isDisabled={donationLocked}
                    {...inputStyles}
                  >
                    {donationOptions.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              ) : null}

              {target?.mode !== "fund_group" ? (
                <>
                  <SimpleGrid columns={{ base: 2, sm: 3, md: 5 }} spacing={3}>
                    {DONATION_PRESETS.map((amount) => {
                      const active = donationAmountCents === amount * 100;
                      return (
                        <Button
                          key={amount}
                          h="58px"
                          borderRadius="14px"
                          bg="white"
                          color={active ? "#a23f00" : "#3c656c"}
                          border="2px solid"
                          borderColor={active ? "#fd6d1a" : "#e4e2dd"}
                          boxShadow={
                            active ? "0 8px 22px rgba(162, 63, 0, 0.12)" : "sm"
                          }
                          fontSize="lg"
                          fontWeight="800"
                          isDisabled={donationLocked}
                          _hover={{
                            borderColor: "#fd6d1a",
                            color: "#a23f00",
                          }}
                          onClick={() => applyPresetDonationAmount(amount)}
                        >
                          ${amount}
                        </Button>
                      );
                    })}
                  </SimpleGrid>

                  <Box
                    border="1px solid"
                    borderColor="#eae8e3"
                    borderRadius="22px"
                    p={{ base: 5, md: 7 }}
                    textAlign="center"
                  >
                    <FormControl isRequired>
                      <FormLabel
                        color="rgba(60, 101, 108, 0.38)"
                        fontSize="xs"
                        fontWeight="800"
                        letterSpacing="0.28em"
                        textTransform="uppercase"
                        justifyContent="center"
                        display="flex"
                      >
                        Secure Donation
                      </FormLabel>
                      <HStack justify="center" spacing={1}>
                        <Text
                          color="rgba(60, 101, 108, 0.18)"
                          fontSize={{ base: "42px", md: "56px" }}
                          fontWeight="800"
                          lineHeight="1"
                        >
                          $
                        </Text>
                        <Input
                          type="number"
                          min="1"
                          step="0.01"
                          value={form.donationAmount}
                          onChange={onChangeField("donationAmount")}
                          placeholder="50"
                          variant="unstyled"
                          textAlign="center"
                          maxW="260px"
                          fontSize={{ base: "48px", md: "64px" }}
                          fontWeight="800"
                          color="#3c656c"
                          lineHeight="1"
                          isDisabled={donationLocked}
                        />
                      </HStack>
                    </FormControl>
                    <Text mt={2} color="rgba(60, 101, 108, 0.45)" fontSize="sm">
                      {impactLabel}
                    </Text>
                  </Box>
                </>
              ) : (
                <Box
                  w="full"
                  borderWidth="1px"
                  borderColor="#e4e2dd"
                  borderRadius="18px"
                  p={{ base: 4, md: 5 }}
                  bg="white"
                  boxShadow="sm"
                >
                  <FormLabel fontWeight="bold" mb={3} color="#3c656c">
                    How much would you like to give?
                  </FormLabel>
                  <VStack spacing={3} align="stretch">
                    {fundAllocations.map((row, idx) => (
                      <Flex
                        key={`${idx}-${row.campaignId}`}
                        gap={2}
                        align="end"
                      >
                        <FormControl>
                          <FormLabel fontSize="xs" color="#3c656c" fontWeight="600">Amount</FormLabel>
                          <Input
                            type="number"
                            min="1"
                            step="0.01"
                            value={row.amount || ""}
                            onChange={(e) =>
                              updateFundAllocationRow(
                                idx,
                                "amount",
                                e.target.value,
                              )
                            }
                            placeholder="0.00"
                            bg="#fbfaf7"
                            borderColor="#e4e2dd"
                            borderRadius="12px"
                            color="#3c656c"
                            isDisabled={donationLocked}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs" color="#3c656c" fontWeight="600">Fund</FormLabel>
                          <Select
                            value={row.campaignId || ""}
                            onChange={(e) =>
                              updateFundAllocationRow(
                                idx,
                                "campaignId",
                                e.target.value,
                              )
                            }
                            placeholder="Select a fund"
                            bg="#fbfaf7"
                            borderColor="#e4e2dd"
                            borderRadius="12px"
                            color="#3c656c"
                            isDisabled={donationLocked}
                          >
                            {donationOptions.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                        <IconButton
                          aria-label="Remove fund row"
                          icon={<Text fontSize="lg">×</Text>}
                          variant="ghost"
                          onClick={() => removeFundAllocationRow(idx)}
                          isDisabled={fundAllocations.length === 1 || donationLocked}
                          colorScheme="red"
                        />
                      </Flex>
                    ))}
                    <Button
                      alignSelf="flex-end"
                      variant="outline"
                      borderColor="#3c656c"
                      color="#3c656c"
                      borderRadius="12px"
                      onClick={addFundAllocationRow}
                      isDisabled={donationLocked}
                      _hover={{ bg: "#fbfaf7" }}
                    >
                      + Another Fund
                    </Button>
                  </VStack>
                </Box>
              )}
            </Stack>
          </Box>

          <Box
            bg="white"
            border="1px solid"
            borderColor="#e4e2dd"
            borderRadius="18px"
            p={{ base: 4, md: 5 }}
            boxShadow="sm"
          >
            <Stack spacing={4}>
              <HStack align="start" spacing={3}>
                <Box
                  w="34px"
                  h="34px"
                  borderRadius="full"
                  bg="#30666f"
                  color="white"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <FiHeart />
                </Box>
                <Box>
                  <Text color="#3c656c" fontWeight="800">
                    Support PurelyGive
                  </Text>
                  <Text color="rgba(60, 101, 108, 0.58)" fontSize="sm">
                    Add an optional tip to help keep fundraising free for
                    nonprofits.
                  </Text>
                </Box>
              </HStack>

              <HStack
                bg="#f5f3ee"
                borderRadius="12px"
                p="1"
                spacing={1}
                flexWrap="wrap"
              >
                {tipConfigNormalized.options.map((option) => {
                  const active = tipMode === option.mode;
                  return (
                    <Button
                      key={option.mode}
                      size="sm"
                      borderRadius="9px"
                      bg={active ? "white" : "transparent"}
                      color={active ? "#a23f00" : "rgba(60, 101, 108, 0.55)"}
                      boxShadow={active ? "sm" : "none"}
                      fontWeight="800"
                      isDisabled={donationLocked}
                      _hover={{ color: "#a23f00" }}
                      onClick={() => selectTipMode(option.mode)}
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </HStack>

              {tipMode === "custom" ? (
                <FormControl>
                  <FormLabel {...labelStyles}>Custom tip amount</FormLabel>
                  <HStack>
                    <Text color="rgba(60, 101, 108, 0.45)" fontWeight="800">
                      $
                    </Text>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={tipCustomAmount}
                      onChange={updateCustomTipAmount}
                      placeholder="0.00"
                      isDisabled={donationLocked}
                      {...inputStyles}
                    />
                  </HStack>
                </FormControl>
              ) : null}

              {showTipNudge ? (
                <HStack
                  align="start"
                  bg="#fffbeb"
                  border="1px solid"
                  borderColor="#fde68a"
                  borderRadius="12px"
                  px={4}
                  py={3}
                  spacing={2}
                >
                  <Box color="#d97706" mt="2px">
                    <FiInfo />
                  </Box>
                  <Box flex="1">
                    <Text color="#92400e" fontSize="sm" fontWeight="600">
                      {tipNudgeMessage}
                    </Text>
                    <Button
                      mt={2}
                      size="sm"
                      variant="outline"
                      colorScheme="orange"
                      onClick={() =>
                        selectTipMode(tipConfigNormalized.defaultMode)
                      }
                      isDisabled={donationLocked}
                    >
                      {tipConfigNormalized.nudge.ctaLabel}
                    </Button>
                  </Box>
                </HStack>
              ) : null}
            </Stack>
          </Box>

          <Box
            bg="white"
            border="1px solid"
            borderColor="#e4e2dd"
            borderRadius="18px"
            p={{ base: 4, md: 5 }}
            boxShadow="sm"
          >
            <Stack spacing={4}>
              <HStack spacing={3}>
                <Box
                  w="34px"
                  h="34px"
                  borderRadius="full"
                  bg="#f0eee9"
                  color="#3c656c"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <FiHeart />
                </Box>
                <Text color="#3c656c" fontWeight="800">
                  Gift note
                </Text>
              </HStack>
              <FormControl>
                <Textarea
                  value={form.note}
                  onChange={onChangeField("note")}
                  placeholder="Share a message with the nonprofit"
                  rows={3}
                  resize="none"
                  isDisabled={donationLocked}
                  {...inputStyles}
                />
              </FormControl>
            </Stack>
          </Box>

          <Box
            bg="white"
            border="1px solid"
            borderColor="#e4e2dd"
            borderRadius="24px"
            p={{ base: 4, md: 6 }}
            boxShadow="0 20px 45px rgba(60, 101, 108, 0.08)"
          >
            <Stack spacing={5}>
              <Box>
                <Text
                  color="#a23f00"
                  fontSize="xs"
                  fontWeight="800"
                  letterSpacing="0.14em"
                  textTransform="uppercase"
                >
                  Donor details
                </Text>
                <Heading mt={1} color="#3c656c" size="md">
                  Contact and billing
                </Heading>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel {...labelStyles}>First Name</FormLabel>
                  <Input
                    value={form.firstName}
                    onChange={onChangeField("firstName")}
                    isDisabled={donationLocked}
                    {...inputStyles}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel {...labelStyles}>Last Name</FormLabel>
                  <Input
                    value={form.lastName}
                    onChange={onChangeField("lastName")}
                    isDisabled={donationLocked}
                    {...inputStyles}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel {...labelStyles}>Email</FormLabel>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={onChangeField("email")}
                    isDisabled={donationLocked}
                    {...inputStyles}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel {...labelStyles}>Phone</FormLabel>
                  <Input
                    value={form.phoneNumber}
                    onChange={onChangeField("phoneNumber")}
                    isDisabled={donationLocked}
                    {...inputStyles}
                  />
                </FormControl>
              </SimpleGrid>
              {/* Address and save profile/payment checkbox options removed */}
            </Stack>
          </Box>

          <Box
            bg="#3c656c"
            color="white"
            borderRadius="24px"
            p={{ base: 5, md: 7 }}
            boxShadow="0 18px 40px rgba(60, 101, 108, 0.2)"
          >
            <Stack spacing={4}>
              <HStack
                bg="rgba(255, 255, 255, 0.1)"
                borderRadius="12px"
                px={3}
                py={2}
                alignSelf="flex-start"
                spacing={2}
              >
                <FiShield />
                <Text
                  color="rgba(255, 255, 255, 0.72)"
                  fontSize="xs"
                  fontWeight="800"
                  letterSpacing="0.1em"
                  textTransform="uppercase"
                >
                  Secure Donation
                </Text>
              </HStack>
              <HStack justify="space-between" align="start" spacing={4}>
                <Text opacity={0.82} minW={0} noOfLines={2}>
                  {displayTitle}
                </Text>
                <Text fontWeight="800" flexShrink={0}>
                  ${centsToDollars(donationAmountCents)}
                </Text>
              </HStack>
              {platformTipCents > 0 ? (
                <HStack justify="space-between" opacity={0.74}>
                  <Text>Platform Tip</Text>
                  <Text fontWeight="800">{summaryTipLabel}</Text>
                </HStack>
              ) : null}
              <Divider borderColor="rgba(255, 255, 255, 0.12)" />
              <HStack justify="space-between" align="end">
                <Text fontWeight="800" fontSize="lg">
                  Total
                </Text>
                <Text fontSize={{ base: "3xl", md: "4xl" }} fontWeight="900">
                  ${centsToDollars(totalCents)}
                </Text>
              </HStack>
            </Stack>
          </Box>

          {!clientSecret ? (
            <Button
              h="58px"
              borderRadius="16px"
              bg="#fd6d1a"
              color="white"
              fontSize="lg"
              fontWeight="900"
              rightIcon={<FiLock />}
              onClick={initializePayment}
              isLoading={intentLoading}
              _hover={{ bg: "#ef5f0d" }}
              _active={{ transform: "scale(0.98)" }}
            >
              Continue to Payment
            </Button>
          ) : null}

          {clientSecret ? (
            <Box
              bg="white"
              border="1px solid"
              borderColor="#e4e2dd"
              borderRadius="24px"
              p={{ base: 4, md: 6 }}
              boxShadow="0 20px 45px rgba(60, 101, 108, 0.08)"
            >
              <Stack spacing={4}>
                <Text
                  color="#a23f00"
                  fontSize="xs"
                  fontWeight="800"
                  letterSpacing="0.14em"
                  textTransform="uppercase"
                >
                  Complete payment
                </Text>
                {stripePromise && completionPayload ? (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret,
                      appearance: {
                        theme: "stripe",
                        variables: {
                          colorPrimary: "#fd6d1a",
                          colorText: "#3c656c",
                          borderRadius: "12px",
                        },
                      },
                    }}
                  >
                    <StripeCheckoutPanel
                      paymentIntentId={paymentIntentId}
                      basePayload={completionPayload}
                      onSuccess={(payload) => {
                        setResult(payload);
                        const redirectTarget = resolveSuccessRedirect();
                        if (redirectTarget) {
                          if (hideCampaignDetails && window.parent !== window) {
                            const targetOrigin = embedParentOrigin || "*";
                            window.parent.postMessage(
                              {
                                source: "purelygive-embed",
                                type: "redirect",
                                url: redirectTarget,
                              },
                              targetOrigin,
                            );
                            setTimeout(() => {
                              window.location.replace(redirectTarget);
                            }, 120);
                            return;
                          }
                          window.location.replace(redirectTarget);
                        }
                      }}
                      onError={setError}
                      isSubmitting={submitting}
                      setIsSubmitting={setSubmitting}
                      requester={requester}
                      returnUrl={stripeReturnUrl}
                    />
                  </Elements>
                ) : (
                  <Alert status="error" borderRadius="14px">
                    <AlertIcon />
                    {missingStripePublishableKeyMessage}
                  </Alert>
                )}
                <Button
                  variant="outline"
                  onClick={resetPayment}
                  isDisabled={submitting}
                >
                  Change Donation Details
                </Button>
              </Stack>
            </Box>
          ) : null}

          <HStack justify="center" color="rgba(60, 101, 108, 0.38)" spacing={7}>
            <FiShield size={24} />
            <FiLock size={24} />
            <FiCheckCircle size={24} />
          </HStack>
          <Text
            color="rgba(60, 101, 108, 0.42)"
            fontSize="xs"
            fontWeight="700"
            textAlign="center"
            textTransform="uppercase"
            letterSpacing="0.08em"
          >
            Secured by Stripe with encrypted payment processing
          </Text>
        </Stack>
      </SimpleGrid>
    </Box>
  );
};

DonationFormStripe.propTypes = {
  campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  orgSlug: PropTypes.string,
  hideCampaignDetails: PropTypes.bool,
  successRedirect: PropTypes.string,
};

export default DonationFormStripe;
